/**
 * Left module sidebar: brand, module search, grouped collapsible modules.
 * Collapsed mode shows icons only (groups become flyout-less icon rails).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getGroups, resolveModule } from './moduleRegistry';
import { MagnifyingGlass, CaretDown, GraduationCap } from '@phosphor-icons/react';
import { BRAND_NAME, BRAND_TAGLINE } from '../../brand';

const APP_VERSION = 'v' + (process.env.REACT_APP_VERSION || '0.31.0');

function useOpenGroups(role, groups) {
  const key = `lms_sidebar_groups_${role}`;
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // default: everything open
    return Object.fromEntries(groups.map((g) => [g.key, true]));
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(open)); } catch { /* ignore */ }
  }, [key, open]);
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  return [open, toggle];
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user, tenantInfo, access } = useAuth();
  const location = useLocation();
  // A role the tenant defined shows only the modules it includes.
  const groups = useMemo(() => getGroups(user?.role, access?.modules), [user?.role, access?.modules]);
  const [openGroups, toggleGroup] = useOpenGroups(user?.role, groups);
  const [query, setQuery] = useState('');

  const active = useMemo(() => resolveModule(location.pathname, user?.role), [location.pathname, user?.role]);

  const q = query.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(q) || g.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length);
  }, [groups, q]);

  const brandName = tenantInfo?.name && user?.role !== 'super_admin' ? tenantInfo.name : BRAND_NAME;
  const preview = !!user?._preview; // "view as a student" window: menu is shown but not navigable

  return (
    <aside
      className={`shell-sidebar flex flex-col h-full border-r border-border bg-white transition-[width] duration-200 ${collapsed ? 'w-14' : 'w-60'}`}
      data-testid="shell-sidebar"
    >
      {/* Brand */}
      <div className={`flex items-center gap-2 h-14 px-3 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <button
          type="button"
          onClick={onToggle}
          className="w-8 h-8 rounded-md bg-shell-primary text-white flex items-center justify-center flex-shrink-0"
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          data-testid="sidebar-toggle"
        >
          <GraduationCap className="w-5 h-5" weight="fill" />
        </button>
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <div className="font-heading font-bold text-[15px] text-shell-primary truncate">{brandName}</div>
            <div className="text-[10px] text-muted-foreground tracking-wide">{BRAND_TAGLINE}</div>
          </div>
        )}
      </div>

      {/* Module search */}
      {!collapsed && (
        <div className="px-3 pt-3 pb-1">
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search modules"
              className="w-full h-9 pl-8 pr-2 rounded-md border border-input bg-muted/40 text-sm outline-none focus:border-shell-primary focus:bg-white"
              data-testid="module-search"
            />
          </div>
        </div>
      )}

      {/* Groups */}
      <nav className="flex-1 overflow-y-auto py-2">
        {visibleGroups.map((g) => {
          const GroupIcon = g.icon;
          const isOpen = q ? true : openGroups[g.key] !== false;
          const groupActive = g.items.some((i) => i.key === active?.key);
          return (
            <div key={g.key} className="mb-1">
              {collapsed ? (
                <div className="mx-2 my-1 border-t border-border/70" />
              ) : (
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  className={`w-full flex items-center gap-2 px-3 h-9 text-[13px] font-semibold tracking-wide ${groupActive ? 'text-shell-primary' : 'text-foreground/80'} hover:bg-muted/60`}
                  data-testid={`nav-group-${g.key}`}
                >
                  <GroupIcon className="w-4 h-4" weight={groupActive ? 'fill' : 'regular'} />
                  <span className="flex-1 text-left">{g.label}</span>
                  <CaretDown className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                </button>
              )}
              {isOpen && (
                <ul>
                  {g.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.key === active?.key;
                    return (
                      <li key={item.key}>
                        <Link
                          to={preview ? '#' : item.path}
                          onClick={preview ? (e) => e.preventDefault() : undefined}
                          aria-disabled={preview || undefined}
                          title={preview ? 'Not available in preview' : collapsed ? item.label : undefined}
                          className={`flex items-center gap-2.5 h-9 text-[13px] transition-colors ${
                            collapsed ? 'justify-center mx-1.5 rounded-md' : 'pl-8 pr-3 mr-2 rounded-r-md'
                          } ${isActive ? 'bg-shell-active text-white font-medium' : 'text-foreground/85 hover:bg-muted/70'}`}
                          data-testid={`nav-${item.key}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" weight={isActive ? 'fill' : 'regular'} />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
        {visibleGroups.length === 0 && (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center">No modules match “{query}”</p>
        )}
      </nav>

      {!collapsed && (
        <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground font-mono flex justify-between">
          <span>{APP_VERSION}</span>
          <span className="capitalize">{preview ? 'Student preview' : user?.role?.replace('_', ' ')}</span>
        </div>
      )}
    </aside>
  );
}
